/**
 * Application Configuration
 * Centralized configuration management with environment variable support
 * Prevents use of direct values throughout the application
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL,
  BASE: import.meta.env.VITE_API_BASE,
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  MAX_RETRY_ATTEMPTS: parseInt(import.meta.env.VITE_API_MAX_RETRIES || '3'),
  RETRY_DELAY: parseInt(import.meta.env.VITE_API_RETRY_DELAY || '1000'),
} as const;

// Firebase Configuration
export const FIREBASE_CONFIG = {
  API_KEY: import.meta.env.VITE_FIREBASE_API_KEY || '',
  AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  APP_ID: import.meta.env.VITE_FIREBASE_APP_ID || '',
} as const;

// Google OAuth Configuration
export const GOOGLE_OAUTH_CONFIG = {
  CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
  REDIRECT_URI: import.meta.env.VITE_GOOGLE_REDIRECT_URI || '',
  SCOPE: import.meta.env.VITE_GOOGLE_OAUTH_SCOPE || 'openid email profile',
} as const;

// Authentication Configuration
export const AUTH_CONFIG = {
  // Enable/disable server-based authentication (email/password via /auth/login)
  ENABLE_SERVER_AUTH: import.meta.env.VITE_ENABLE_SERVER_AUTH !== 'false',

  // Enable/disable Firebase OAuth (client-side Firebase SDK + backend verification)
  ENABLE_FIREBASE_AUTH: import.meta.env.VITE_ENABLE_FIREBASE_AUTH !== 'false',

  // Enable/disable Google OAuth (direct backend OAuth flow via /auth/google)
  ENABLE_GOOGLE_OAUTH: import.meta.env.VITE_ENABLE_GOOGLE_OAUTH === 'true',

  TOKEN_STORAGE_KEY: 'auth_token',
  REFRESH_TOKEN_STORAGE_KEY: 'auth_refresh_token',
  USER_STORAGE_KEY: 'auth_user',
  EXPIRES_AT_STORAGE_KEY: 'auth_expires_at',
  TOKEN_REFRESH_BUFFER: parseInt(import.meta.env.VITE_TOKEN_REFRESH_BUFFER || '300000'), // 5 minutes
} as const;

// Application Configuration
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || 'PromptShare',
  DESCRIPTION: import.meta.env.VITE_APP_DESCRIPTION || 'AI Prompt Sharing Platform',
  VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
  ENVIRONMENT: import.meta.env.NODE_ENV || 'production',
  BASE_URL: import.meta.env.VITE_APP_BASE_URL || 'http://promptshare.pro',
  CONTACT_EMAIL: import.meta.env.VITE_CONTACT_EMAIL || 'contact@promptshare.pro',
  STUDIO_URL: import.meta.env.VITE_STUDIO_URL || 'https://studio.promptshare.pro',
  VERSION_CHECK_INTERVAL: parseInt(import.meta.env.VITE_VERSION_CHECK_INTERVAL || '10800000'), // default 3 hours
} as const;

// UI Configuration
export const UI_CONFIG = {
  TOAST_DURATION: parseInt(import.meta.env.VITE_TOAST_DURATION || '4000'),
  ANIMATION_DURATION: parseInt(import.meta.env.VITE_ANIMATION_DURATION || '200'),
  DEBOUNCE_DELAY: parseInt(import.meta.env.VITE_DEBOUNCE_DELAY || '500'),
  PAGINATION_SIZE: parseInt(import.meta.env.VITE_PAGINATION_SIZE || '20'),
  MAX_FILE_SIZE: parseInt(import.meta.env.VITE_MAX_FILE_SIZE || '7340032'), // 7MB
  PROMPT_TEXT_MIN_LENGTH: parseInt(import.meta.env.VITE_PROMPT_TEXT_MIN_LENGTH || '10'),
  PROMPT_TEXT_MAX_LENGTH: parseInt(import.meta.env.VITE_PROMPT_TEXT_MAX_LENGTH || '5000'),
  PROMPT_TEXT_WARNING_THRESHOLD: parseInt(import.meta.env.VITE_PROMPT_TEXT_WARNING_THRESHOLD || '3750'),
  PROMPT_TEXT_ALERT_THRESHOLD: parseInt(import.meta.env.VITE_PROMPT_TEXT_ALERT_THRESHOLD || '4500'),
  FORM_AUTOSAVE_DELAY: parseInt(import.meta.env.VITE_FORM_AUTOSAVE_DELAY || '2000'),
  PREVIEW_URL_MAX_LENGTH: parseInt(import.meta.env.VITE_PREVIEW_URL_MAX_LENGTH || '1500'),
  ADMIN_BYPASS_PROMPT_MAX_LENGTH: parseInt(import.meta.env.VITE_ADMIN_BYPASS_PROMPT_MAX_LENGTH || '10000'),
  ADMIN_BYPASS_TAGS_MAX: parseInt(import.meta.env.VITE_ADMIN_BYPASS_TAGS_MAX || '49'),
} as const;

// Premium Content Configuration
export const PREMIUM_CONTENT_CONFIG = {
  // Premium Photos
  PHOTO_MAX_SIZE_MB: parseInt(import.meta.env.VITE_PREMIUM_PHOTO_MAX_SIZE_MB || '10'),
  PHOTO_MAX_COUNT: parseInt(import.meta.env.VITE_PREMIUM_PHOTO_MAX_COUNT || '20'),
  PHOTO_ALLOWED_TYPES: (import.meta.env.VITE_PREMIUM_PHOTO_ALLOWED_TYPES || 'jpg,jpeg,png,gif,webp').split(','),

  // Premium Videos
  VIDEO_MAX_SIZE_MB: parseInt(import.meta.env.VITE_PREMIUM_VIDEO_MAX_SIZE_MB || '512'),
  VIDEO_MAX_COUNT: parseInt(import.meta.env.VITE_PREMIUM_VIDEO_MAX_COUNT || '5'),
  VIDEO_ALLOWED_TYPES: (import.meta.env.VITE_PREMIUM_VIDEO_ALLOWED_TYPES || 'mp4,webm,mov,avi,mkv').split(','),

  // Common
  TITLE_MIN_LENGTH: parseInt(import.meta.env.VITE_PREMIUM_TITLE_MIN_LENGTH || '3'),
  TITLE_MAX_LENGTH: parseInt(import.meta.env.VITE_PREMIUM_TITLE_MAX_LENGTH || '255'),
  DESCRIPTION_MAX_LENGTH: parseInt(import.meta.env.VITE_PREMIUM_DESCRIPTION_MAX_LENGTH || '1000'),
  PROMPT_MIN_LENGTH: parseInt(import.meta.env.VITE_PREMIUM_PROMPT_MIN_LENGTH || '10'),
  PROMPT_MAX_LENGTH: parseInt(import.meta.env.VITE_PREMIUM_PROMPT_MAX_LENGTH || '5000'),
  PROMPT_WARNING_THRESHOLD: parseInt(import.meta.env.VITE_PREMIUM_PROMPT_WARNING_THRESHOLD || '3750'),
  PROMPT_ALERT_THRESHOLD: parseInt(import.meta.env.VITE_PREMIUM_PROMPT_ALERT_THRESHOLD || '4500'),
  MAX_TAGS: parseInt(import.meta.env.VITE_PREMIUM_MAX_TAGS || '10'),
  ADMIN_BYPASS_PROMPT_MAX_LENGTH: parseInt(import.meta.env.VITE_ADMIN_BYPASS_PROMPT_MAX_LENGTH || '10000'),
  ADMIN_BYPASS_TAGS_MAX: parseInt(import.meta.env.VITE_ADMIN_BYPASS_TAGS_MAX || '49'),
} as const;

// Feature Flags
export const FEATURES = {
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  ENABLE_ERROR_REPORTING: import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true',
  ENABLE_PWA: import.meta.env.VITE_ENABLE_PWA !== 'false',
  ENABLE_OFFLINE_MODE: import.meta.env.VITE_ENABLE_OFFLINE_MODE === 'true',
  ENABLE_SEARCH: import.meta.env.VITE_ENABLE_SEARCH !== 'false',
  ENABLE_COMMENTS: import.meta.env.VITE_ENABLE_COMMENTS !== 'false',
  ENABLE_SOCIAL_SHARING: import.meta.env.VITE_ENABLE_SOCIAL_SHARING !== 'false',
} as const;

// External Services Configuration
export const SERVICES_CONFIG = {
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN || '',
  GOOGLE_ANALYTICS_ID: import.meta.env.VITE_GOOGLE_ANALYTICS_ID || '',
  POSTHOG_KEY: import.meta.env.VITE_POSTHOG_KEY || '',
  POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
} as const;

// Logging Configuration
export const LOG_CONFIG = {
  ENABLED: import.meta.env.VITE_LOG_ENABLED !== 'false',
  PREFIX: import.meta.env.VITE_LOG_PREFIX || 'APP',
} as const;

// Validation
export const validateConfig = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check required Firebase config if Firebase Auth is enabled
  if (AUTH_CONFIG.ENABLE_FIREBASE_AUTH) {
    if (!FIREBASE_CONFIG.API_KEY) errors.push('VITE_FIREBASE_API_KEY is required when Firebase Auth is enabled');
    if (!FIREBASE_CONFIG.AUTH_DOMAIN) errors.push('VITE_FIREBASE_AUTH_DOMAIN is required when Firebase Auth is enabled');
    if (!FIREBASE_CONFIG.PROJECT_ID) errors.push('VITE_FIREBASE_PROJECT_ID is required when Firebase Auth is enabled');
  }

  // Check API configuration
  if (!API_CONFIG.BASE_URL) errors.push('VITE_API_BASE_URL is required');
  if (!API_CONFIG.BASE) errors.push('VITE_API_BASE is required');

  // Validate numeric values
  if (API_CONFIG.TIMEOUT < 1000) errors.push('VITE_API_TIMEOUT must be at least 1000ms');
  if (API_CONFIG.MAX_RETRY_ATTEMPTS < 0) errors.push('VITE_API_MAX_RETRIES must be non-negative');

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Run strict validation immediately on module load
const validation = validateConfig();
if (!validation.isValid) {
  const errorMsg = `[Configuration Error] Critical environment variables are missing or invalid:\n` +
    validation.errors.map(err => `  - ${err}`).join('\n') +
    `\n\nPlease check your .env file or system environment variables.`;
  console.error(errorMsg);
  throw new Error(errorMsg);
}

// Development helpers
export const isDevelopment = () => APP_CONFIG.ENVIRONMENT === 'development';
export const isProduction = () => APP_CONFIG.ENVIRONMENT === 'production';

// Export all configurations
export default {
  API: API_CONFIG,
  FIREBASE: FIREBASE_CONFIG,
  GOOGLE_OAUTH: GOOGLE_OAUTH_CONFIG,
  AUTH: AUTH_CONFIG,
  APP: APP_CONFIG,
  UI: UI_CONFIG,
  PREMIUM_CONTENT: PREMIUM_CONTENT_CONFIG,
  FEATURES,
  SERVICES: SERVICES_CONFIG,
  LOG: LOG_CONFIG,
  validateConfig,
  isDevelopment,
  isProduction,
};